import { useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useGetAnnotationJobStatusesQuery, AnnotateApi } from '@/services/application/annotateReducer'
import { store } from '@/store'
import { updateJobStatus } from '@/pages/EngagementDetails/ApplicationDetail/Annotation/AnnotateSlice'
import {
  setProcessingEvidenceIds,
  removeProcessingEvidenceId,
  setPendingJobCountByEvidenceId,
  deactivatePolling,
} from '@/features/annotationPolling/annotationPollingSlice'

/**
 * Global annotation job poller.
 * Mount once at the authenticated app level.
 *
 * Polling lifecycle:
 * - Off by default (isPollingActive = false in Redux)
 * - Activates when user hits Mark (dispatch activatePolling())
 * - Polls every 10s
 * - Pauses automatically when server returns empty array (no pending jobs)
 */
export function useAnnotationJobPoller(enabled: boolean) {
  const dispatch = useDispatch()
  const annotateState = useSelector((state: any) => state.Annotate)
  const isPollingActive = useSelector((state: any) => state.annotationPolling.isPollingActive)
  const resolvingRef = useRef<Set<string>>(new Set())

  const shouldPoll = enabled && isPollingActive

  const { data: pendingFromServer, isFetching } = useGetAnnotationJobStatusesQuery(undefined, {
    skip: !shouldPoll,
    pollingInterval: shouldPoll ? 10000 : 0,
    refetchOnMountOrArgChange: true,  // force a fresh fetch when shouldPoll flips to true
  })

  useEffect(() => {
    // Wait for a real server response — never act on stale cached data.
    // When polling reactivates after a previous session, RTK Query immediately
    // returns the cached [] while fetching fresh data. Without this guard the
    // "done" path would fire on the old cache, flip chips to Completed and clear
    // the evidence-table spinner before the server has even responded.
    if (!shouldPoll || pendingFromServer === undefined || isFetching) return

    // Collect all Redux entries that are still in-flight (non-terminal annotateStatus).
    const inFlightInRedux = (annotateState as any[]).flatMap((entry: any) =>
      (entry.annotateList as any[])
        .filter(
          (a: any) =>
            a.jobId &&
            !['Completed', 'Failed', 'TextExtractionFailed', 'TextEvaluationFailed'].includes(
              entry.annotateStatus,
            ),
        )
        .map((a: any) => ({ jobId: a.jobId as string, entry })),
    )

    // Treat jobs as done only after they leave the server-side pending list.
    // Annotation details depend on evaluated values, so TextExtracted is not
    // sufficient for the FE to consider an annotation complete.
    if (pendingFromServer.length === 0) {
      // Flip every remaining in-flight Redux chip to Completed so spinners clear.
      inFlightInRedux.forEach(({ jobId, entry }) => {
        dispatch(
          updateJobStatus({
            annotationTypeId: entry.annotationTypeId,
            status: 'Completed',
            jobId,
            pageNumber: entry.pageNumber,
          }),
        )
      })
      resolvingRef.current.clear()
      dispatch(setProcessingEvidenceIds([]))
      dispatch(setPendingJobCountByEvidenceId({}))
      dispatch(deactivatePolling())
      return
    }

    // Still jobs pending — keep spinner alive for affected evidence IDs.
    // Merge server evidenceIds + Redux in-flight evidenceIds (covers the transient gap
    // where a NotStarted job briefly disappears from the server list).
    const serverEvidIds = new Set(
      pendingFromServer.map((j: any) => j.evidenceId as string).filter(Boolean),
    )
    inFlightInRedux.forEach(({ entry }) => {
      if (entry.evidenceId) serverEvidIds.add(entry.evidenceId as string)
    })
    dispatch(setProcessingEvidenceIds([...serverEvidIds]))

    // Build per-evidence count for tooltip
    const countByEvidence: Record<string, number> = {}
    pendingFromServer.forEach((j: any) => {
      if (j.evidenceId) {
        countByEvidence[j.evidenceId as string] = (countByEvidence[j.evidenceId as string] ?? 0) + 1
      }
    })
    dispatch(setPendingJobCountByEvidenceId(countByEvidence))

    const pendingIdsOnServer = new Set(pendingFromServer.map((j: any) => j.jobId as string))

    inFlightInRedux.forEach(({ jobId, entry }) => {
      if (pendingIdsOnServer.has(jobId)) return       // still pending on server — wait
      if (resolvingRef.current.has(jobId)) return     // individual fetch already in flight

      resolvingRef.current.add(jobId)

      // Job disappeared from the pending list — fetch its concrete final status.
      store.dispatch(AnnotateApi.endpoints.getJobStatus.initiate(jobId))
        .unwrap()
        .then((result: { statusId: number; status: string }) => {
          // statusId 0 = still in transition (NotStarted/InProgress/TextExtracted/TextEvaluated).
          // It will reappear on the next poll; spinner stays via inFlightInRedux merge above.
          if (result.statusId === 0) return
          dispatch(
            updateJobStatus({
              annotationTypeId: entry.annotationTypeId,
              status: result.statusId === 1 ? 'Completed' : 'Failed',
              jobId,
              pageNumber: entry.pageNumber,
            }),
          )
          dispatch(removeProcessingEvidenceId(entry.evidenceId))
        })
        .catch(() => {
          dispatch(
            updateJobStatus({
              annotationTypeId: entry.annotationTypeId,
              status: 'Failed',
              jobId,
              pageNumber: entry.pageNumber,
            }),
          )
          dispatch(removeProcessingEvidenceId(entry.evidenceId))
        })
        .finally(() => {
          resolvingRef.current.delete(jobId)
        })
    })
  }, [pendingFromServer, shouldPoll, isFetching])
}

